/**
 * Survey format detection and normalization.
 *
 * The API can return two shapes for raw.survey:
 * - Legacy: an array of questions [{ number, type, question, type_config, conditional?, conditional_config?, next_question? }] used as-is by the runner.
 * - Card-v1 (new): an object { format: "card-v1", questions, paths } that must be normalized to the legacy array shape.
 *
 * The runner always consumes the same shape (legacy array). Use toRunnerSurvey(payload) as the single entry point;
 * it detects the format and returns the legacy array for both.
 */

const CARD_V1_FORMAT = 'card-v1';

/** Question types the runner can render. Legacy payloads are filtered to these. */
const ALLOWED_TYPES = ['radio', 'checkbox', 'rating', 'textfield', 'done'];

/**
 * Whether the survey payload is the card-v1 (new) format.
 * @param {object} survey - Card-v1 object (e.g. raw.survey when API returns { id, survey: { format, questions, paths } })
 * @returns {boolean}
 */
export function isCardV1Format(survey) {
    return Boolean(
        survey &&
        typeof survey === 'object' &&
        !Array.isArray(survey) &&
        (survey.format === CARD_V1_FORMAT || survey.surveyFormat === 'new') &&
        Array.isArray(survey.questions) &&
        survey.questions.length > 0
    );
}

/**
 * Single entry point: convert API survey payload to the runner format (legacy array).
 * Accepts either legacy (array) or card-v1 (object); returns the legacy array or null if invalid.
 * @param {object|array} payload - raw.survey from API (legacy array or card-v1 object)
 * @returns {array|null} Legacy survey array for the runner, or null if payload is invalid
 */
export function toRunnerSurvey(payload) {
    if (!payload) return null;
    if (isCardV1Format(payload)) return normalizeCardV1ToLegacy(payload);
    if (!Array.isArray(payload) || payload.length === 0) return null;
    const filtered = payload.filter((q) => {
        const ok = q && typeof q === 'object' && ALLOWED_TYPES.includes(q.type);
        if (!ok && process.env.NODE_ENV !== 'production' && q && typeof q === 'object') {
            console.warn('surveyFormats: skipping question with unknown or missing type', q.type, q);
        }
        return ok;
    });
    return filtered.length > 0 ? filtered : null;
}

/**
 * Map new format config to legacy type_config.
 * @param {string} type - question type
 * @param {object} config - new format config (e.g. { options: [...] })
 * @returns {object} legacy type_config
 */
function configToTypeConfig(type, config) {
    if (!config) return {};
    if (type === 'radio' && config.options) {
        return { radio_options: config.options };
    }
    if (type === 'checkbox' && config.options) {
        return { checkbox_options: config.options };
    }
    if (type === 'rating') {
        const rateAmount = config.rate_amount ?? config.rateAmount ?? 5;
        const format = config.format || 'numerical';
        return { format, rate_amount: rateAmount };
    }
    if (process.env.NODE_ENV !== 'production' && !ALLOWED_TYPES.includes(type)) {
        console.warn('surveyFormats: unknown question type in configToTypeConfig', type);
    }
    return config.type_config || {};
}

/**
 * Build one legacy conditional_config entry (single condition value, single target).
 * next_question_index is used when the next step has a duplicate number so the runner navigates to the correct question (e.g. Path 4 T3 -> Numerical, not T2).
 * @param {string} conditionValue - trigger value that matches this config (e.g. "1", "Yes")
 * @param {object} target - card-v1 question object (target of path step)
 * @param {number|string} nextQuestion - question number to go to after this step
 * @param {number} [nextQuestionIndex] - index in the survey array of the next question (used when duplicate numbers exist)
 * @returns {object} legacy conditional_config entry
 */
function buildConditionalConfigEntry(conditionValue, target, nextQuestion, nextQuestionIndex) {
    if (!target) return null;
    const entry = {
        condition: conditionValue,
        number: target.number,
        response_question: (target.text !== undefined && target.text !== null) ? target.text : (target.question || ''),
        next_question: nextQuestion,
        type: target.type,
        type_config: configToTypeConfig(target.type, target.config || {}),
    };
    if (nextQuestionIndex !== undefined && nextQuestionIndex !== null && nextQuestionIndex >= 0) {
        entry.next_question_index = nextQuestionIndex;
    }
    return entry;
}

/**
 * Convert card-v1 survey to legacy survey array for the runner.
 * Uses paths for conditional branching; flowFormat is not required for display.
 *
 * @param {object} cardV1Survey - { format, questions, paths }
 * @returns {array} Legacy survey array [{ number, type, question, type_config, conditional?, conditional_config?, next_question? }]
 */
export function normalizeCardV1ToLegacy(cardV1Survey) {
    const questions = cardV1Survey.questions || [];
    const paths = cardV1Survey.paths || [];

    const doneEntry = questions.find((q) => q.type === 'done');
    const questionEntries = questions.filter((q) => q.type !== 'done');
    const allowedQuestionEntries = questionEntries.filter((q) => {
        const ok = ALLOWED_TYPES.includes(q.type);
        if (!ok && process.env.NODE_ENV !== 'production') {
            console.warn('surveyFormats: skipping card-v1 question with unknown type', q.type, q);
        }
        return ok;
    });

    const questionsById = new Map();
    allowedQuestionEntries.forEach((q) => {
        if (q.id !== undefined && q.id !== null) questionsById.set(String(q.id), q);
    });
    if (doneEntry) {
        questionsById.set('done', doneEntry);
    }

    const toNum = (n) => (n !== undefined && n !== null && n !== '') ? Number(n) : NaN;

    const pathTargetNumbers = new Set();
    paths.forEach((path) => {
        (path.questionIds || []).forEach((id) => {
            const q = questionsById.get(String(id));
            if (q && q.number !== undefined && q.number !== null) {
                const num = toNum(q.number);
                if (!Number.isNaN(num)) pathTargetNumbers.add(num);
            }
        });
    });

    const ordered = [...allowedQuestionEntries].sort((a, b) => {
        const numA = toNum(a.number);
        const numB = toNum(b.number);
        return numA - numB;
    });
    if (doneEntry) ordered.push(doneEntry);

    const orderedNumbers = ordered.map((q) => toNum(q.number));
    const mainFlowNumbers = orderedNumbers.filter((num) => !Number.isNaN(num) && !pathTargetNumbers.has(num));

    const getNextInMainFlow = (currentNumber) => {
        const current = toNum(currentNumber);
        if (Number.isNaN(current)) return undefined;
        const inMain = mainFlowNumbers.indexOf(current);
        if (inMain !== -1 && inMain < mainFlowNumbers.length - 1) {
            return mainFlowNumbers[inMain + 1];
        }
        const idx = orderedNumbers.indexOf(current);
        if (idx === -1 || idx >= orderedNumbers.length - 1) return undefined;
        for (let i = idx + 1; i < orderedNumbers.length; i++) {
            const n = orderedNumbers[i];
            if (!Number.isNaN(n) && mainFlowNumbers.includes(n)) return n;
        }
        return undefined;
    };

    // Sort paths deterministically so that "last path wins" is consistent across runs and API ordering.
    const sortedPaths = [...paths].sort((a, b) => {
        const aTriggerId = a.trigger?.questionId ?? '';
        const bTriggerId = b.trigger?.questionId ?? '';
        if (String(aTriggerId) !== String(bTriggerId)) {
            return String(aTriggerId).localeCompare(String(bTriggerId));
        }
        const aFirstQ = (a.questionIds ?? [])[0] ?? '';
        const bFirstQ = (b.questionIds ?? [])[0] ?? '';
        return String(aFirstQ).localeCompare(String(bFirstQ));
    });

    const pathsByTriggerQuestionId = new Map();
    sortedPaths.forEach((path) => {
        const qId = path.trigger && path.trigger.questionId;
        if (qId === undefined || qId === null || qId === '') return;
        const key = String(qId);
        if (!pathsByTriggerQuestionId.has(key)) {
            pathsByTriggerQuestionId.set(key, []);
        }
        pathsByTriggerQuestionId.get(key).push(path);
    });

    // For path-target questions that are not the last in their path, next should be the next step in that path (e.g. Numerical -> Satisfaction), not the main flow.
    // If a question appears as non-last in more than one path, last path processed wins (deterministic after sortedPaths).
    const pathNextByQuestionId = new Map();
    sortedPaths.forEach((path) => {
        const questionIds = path.questionIds ?? [];
        for (let i = 0; i < questionIds.length - 1; i++) {
            const currentId = String(questionIds[i]);
            const nextId = questionIds[i + 1];
            const nextQ = questionsById.get(String(nextId));
            if (!nextQ) continue;
            const nextIndex = ordered.findIndex((oq) => String(oq.id) === String(nextId));
            if (nextIndex >= 0) {
                if (process.env.NODE_ENV !== 'production' && pathNextByQuestionId.has(currentId)) {
                    console.warn('surveyFormats: pathNextByQuestionId overwrite for', currentId,
                        'old:', pathNextByQuestionId.get(currentId),
                        'new:', { number: nextQ.number, index: nextIndex });
                }
                pathNextByQuestionId.set(currentId, { number: nextQ.number, index: nextIndex });
            }
        }
    });

    const legacy = ordered.map((q) => {
        const isDone = q.type === 'done';
        const num = toNum(q.number);
        const legacyQuestion = {
            number: !Number.isNaN(num) ? num : q.number,
            type: q.type,
            question: (q.text !== undefined && q.text !== null) ? q.text : (q.question || ''),
            type_config: configToTypeConfig(q.type, q.config || {}),
        };

        if (isDone) {
            return legacyQuestion;
        }

        const pathNext = pathNextByQuestionId.get(String(q.id));
        let nextInOrder = pathNext
            ? pathNext.number
            : getNextInMainFlow(q.number);
        if ((nextInOrder === undefined || nextInOrder === null) && pathTargetNumbers.has(legacyQuestion.number) && ordered.length > 0) {
            const lastNum = toNum(ordered[ordered.length - 1].number);
            if (!Number.isNaN(lastNum)) nextInOrder = lastNum;
        }
        if (nextInOrder !== undefined && nextInOrder !== null) {
            legacyQuestion.next_question = nextInOrder;
        }
        if (pathNext && pathNext.index >= 0) {
            legacyQuestion.next_question_index = pathNext.index;
        }

        const triggerPaths = pathsByTriggerQuestionId.get(String(q.id));
        if (triggerPaths && triggerPaths.length > 0) {
            legacyQuestion.conditional = true;
            // Build configs: support multi-value triggers (e.g. values ["1","2"] → one config per value) and
            // multi-question paths (e.g. questionIds [A,B,C] → one config per step, next_question chains through path).
            // Dedupe by condition+targetId. When two paths target the same (value, question), we prefer the config
            // where condition === "Yes" so that Yes/No branching (e.g. survey 1 Q6) resolves to the Yes path.
            const configsMap = new Map();
            const nextAfterPath = nextInOrder;
            const sortedTriggerPaths = [...triggerPaths].sort((a, b) => {
                const aTriggerId = a.trigger?.questionId ?? '';
                const bTriggerId = b.trigger?.questionId ?? '';
                if (String(aTriggerId) !== String(bTriggerId)) {
                    return String(aTriggerId).localeCompare(String(bTriggerId));
                }
                const aFirstQ = (a.questionIds ?? [])[0] ?? '';
                const bFirstQ = (b.questionIds ?? [])[0] ?? '';
                return String(aFirstQ).localeCompare(String(bFirstQ));
            });
            sortedTriggerPaths.forEach((path) => {
                const values = path.trigger?.values ?? [];
                const questionIds = path.questionIds ?? [];
                if (values.length === 0 || questionIds.length === 0) return;
                for (let i = 0; i < questionIds.length; i++) {
                    const targetId = questionIds[i];
                    const target = questionsById.get(String(targetId));
                    if (!target) continue;
                    const nextInPath = i < questionIds.length - 1
                        ? (questionsById.get(String(questionIds[i + 1])) || {}).number
                        : nextAfterPath;
                    if (nextInPath === undefined || nextInPath === null) continue;
                    const nextQuestionIndex = i < questionIds.length - 1
                        ? ordered.findIndex((oq) => String(oq.id) === String(questionIds[i + 1]))
                        : ordered.findIndex((oq) => oq.number === nextAfterPath && mainFlowNumbers.includes(toNum(oq.number)));
                    values.forEach((v) => {
                        const key = `${v}-${targetId}`;
                        const config = buildConditionalConfigEntry(v, target, nextInPath, nextQuestionIndex >= 0 ? nextQuestionIndex : undefined);
                        if (!config) return;
                        const existing = configsMap.get(key);
                        // Prefer "Yes" when duplicate (value, target): ensures Yes/No flows choose Yes path.
                        if (!existing || (v === 'Yes' && existing.condition !== 'Yes')) {
                            configsMap.set(key, config);
                        }
                    });
                }
            });
            const configs = Array.from(configsMap.values());
            legacyQuestion.conditional_config =
                configs.length === 1 ? configs[0] : configs;
        }

        return legacyQuestion;
    });

    return legacy;
}
